import { useCallback, useEffect, useRef, useState } from "react";
import { useMediaQuery } from "react-responsive";
import { useRouter } from "next/navigation";
import { notifications } from "@mantine/notifications";
import { useSearchTips } from "@/hooks/useSearchTips";
import SearchTipsModal from "@/components/SearchTipsModal";

import { useSearchContext } from "@/context/searchContext";
import { useAuthContext } from "@/context/authContext";
import { useGlobalData } from "@/context/globalContext";
import { generateBrandSlug, generateVendorSlug } from "@/lib/shared/utils";
import { useFiltersStore } from "@/stores/filtersStore";
import { useImageSearchStore } from "@/stores/imageSearchStore";
import { useItemsStore } from "@/stores/itemsStore";
import { useUiStore } from "@/stores/uiStore";
import { useTokenBudgetStore } from "@/stores/tokenBudgetStore";
import { getAndClearLastHoveredItemId } from "@/lib/client/hoverTracking";
import { decompressFilters, migrateFilterFormat, isValidActiveFilters } from "@/lib/client/filters";
import { useImageSearch } from "@/hooks/useImageSearch";
import { useFilterAnnouncements } from "@/hooks/useFilterAnnouncements";
import { useScrollDepthTracking } from "@/hooks/useScrollDepthTracking";
import { updateFilterPosition } from "@/lib/client/api";
import FilterMenu from "@/components/FilterMenu";
import CardGrid from "@/components/CardGrid";
import VirtualizedCardGrid from "@/components/CardGrid/VirtualizedCardGrid";
import MobileMenu from "@/components/layout/MobileMenu";
import SignInPrompt from "@/components/SignInPrompt";
import ImageSearchBanner from "@/components/ImageSearchBanner";
import KurboPlaceholderCard from "@/components/KurboPlaceholderCard";
import { NoResultsRescue, FeedEndCta } from "@/components/SearchRescue";
import { TOKEN_UI_ENABLED } from "@/lib/shared/featureFlags";
import styles from "./styles.module.scss";
import { Box as MantineBox } from "@mantine/core";

type Props = {
  brand?: string;
  vendor?: string;
  style?: string;
  type?: string;
  searchText?: string;
  regions?: string[];
  brands?: string[];
  vendors?: string[];
  keywords?: string[];
  filterId?: number;
  enableImageSearch?: boolean;
  clearFilters?: boolean;
  fromNotification?: boolean;
  imageSearchUrl?: string;
  sharedFilterToken?: string;
};

const API_URL = process.env.NEXT_PUBLIC_API_URL || "";

const PageContent = ({
  brand,
  vendor,
  type,
  searchText,
  regions,
  brands,
  vendors,
  keywords,
  filterId,
  enableImageSearch = true,
  clearFilters,
  fromNotification,
  imageSearchUrl,
  sharedFilterToken,
}: Props) => {
  const { isSignedIn } = useAuthContext();
  const itemModalOpen = useUiStore((state) => state.itemModalOpen);
  const favoritesModalOpen = useUiStore((state) => state.favoritesModalOpen);
  const priceMonitorOpen = useUiStore((state) => state.priceMonitorOpen);
  const slideInItem = useUiStore((state) => state.slideInItem);

  // On open: after the grid reflows narrower, scroll so the clicked item is visible.
  // On close: after the grid reflows wider, restore to wherever the user was when they closed.
  const scrollAnchorRef = useRef<number | null>(null);
  const [scrollToItemId, setScrollToItemId] = useState<number | null>(null);

  useEffect(() => {
    if (itemModalOpen && slideInItem?.id) {
      scrollAnchorRef.current = slideInItem.id;
      setScrollToItemId(slideInItem.id);
    } else if (!itemModalOpen && scrollAnchorRef.current) {
      scrollAnchorRef.current = null;
      // On desktop (≥1251px) scroll to the last item the user hovered while the
      // slide-in was open. On tablet/mobile the slide-in is full-screen so hover
      // tracking is meaningless — fall back to restoring the first visible item.
      const isDesktop = window.innerWidth >= 1251;
      setScrollToItemId(isDesktop ? getAndClearLastHoveredItemId() : null);
    }
  }, [itemModalOpen, slideInItem?.id]);

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const {
    isActive: imageSearchActive,
    loading: imageSearchLoading,
    loadingMore: imageSearchLoadingMore,
    items: imageSearchItems,
  } = useImageSearch(scrollContainerRef);

  const [savedFilterRestored, setSavedFilterRestored] = useState(false);
  const [shareRedeemFailed, setShareRedeemFailed] = useState(false);
  const {
    items,
    loadMore,
    loading,
    setPreloadBrand,
    setPreloadVendor,
    setPreloadedType,
    setPreloadedFilters,
    updateKeywordSearch,
    noResults,
    activeFilters,
    selectSavedFilter,
    savedFilters,
  } = useSearchContext();
  const { brands: loadedBrands, vendors: loadedVendors, exchangeRates } = useGlobalData();
  const { startSearch: startImageSearch } = useImageSearchStore((state) => state.actions);
  const showSignInPrompt = useItemsStore((state) => state.showSignInPrompt);
  const hasReachedEnd = useItemsStore((state) => state.hasReachedEnd);
  const recentItemCount = useItemsStore((state) => state.recentItemCount);
  const tokensRemaining = useTokenBudgetStore((s) => s.tokensRemaining);
  const tokensInitialized = useTokenBudgetStore((s) => s.initialized);
  const kurboMode = useFiltersStore((state) => state.kurboMode);
  const savedFilterId = useFiltersStore((state) => state.savedFilterId);
  const setKurboMode = useFiltersStore((state) => state.actions.setKurboMode);
  const isMobile = useMediaQuery({ query: "(max-width: 1250px)" });
  const setShowSignInPrompt = useItemsStore((state) => state.actions.setShowSignInPrompt);
  const resetActiveFilters = useFiltersStore((state) => state.actions.resetActiveFilters);
  const applySharedFilter = useFiltersStore((state) => state.actions.applySharedFilter);
  const router = useRouter();
  const lastSavedResumeTimestampRef = useRef<string | null>(null);

  const handleResumePositionChange = useCallback(
    (item: SearchResultItem) => {
      if (!savedFilterId || imageSearchActive) {
        console.log("Skipping resume position update: no saved filter or image search active");
        return;
      }

      const sortValue = activeFilters.sort?.[0]?.value ?? "newest";
      if (sortValue === "lowest" || sortValue === "highest") {
        return;
      }

      const resumeTimestamp = item.solddate || item.itemtimestamp;
      if (!resumeTimestamp || resumeTimestamp === lastSavedResumeTimestampRef.current) {
        console.log(
          "Skipping resume position update: no valid resume timestamp or already updated"
        );
        return;
      }

      lastSavedResumeTimestampRef.current = resumeTimestamp;
      updateFilterPosition(savedFilterId, resumeTimestamp);
      const currentFilters = useFiltersStore.getState().savedFilters;
      if (currentFilters.length > 0) {
        useFiltersStore
          .getState()
          .actions.setSavedFilters(
            currentFilters.map((f) =>
              f.id === savedFilterId ? { ...f, resume_from: resumeTimestamp } : f
            )
          );
      }
    },
    [activeFilters.sort, imageSearchActive, savedFilterId]
  );

  // When arriving from a notification link (?kurbo=1), force KURBO mode on once,
  // then strip just that param so a refresh doesn't re-trigger it.
  // window.history.replaceState is synchronous (unlike router.replace) and preserves
  // other params like ?filterid so the saved filter stays loaded on refresh.
  useEffect(() => {
    if (fromNotification) {
      setKurboMode(true);
      const url = new URL(window.location.href);
      url.searchParams.delete("kurbo");
      window.history.replaceState(null, "", url.toString());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When arriving via ?imageSearchUrl=<encoded image url> (e.g. a Discord
  // post's "Find Similar" button), kick off the same image search the FIND
  // MORE button triggers in-app — sourced from a URL instead of an item
  // already in memory, so only imageurl is populated on the synthetic
  // source item (the banner only ever reads sourceItem.imageurl).
  useEffect(() => {
    if (imageSearchUrl) {
      startImageSearch(
        { imageurl: imageSearchUrl } as SearchResultItem,
        null,
        undefined,
        exchangeRates as Record<string, number> | undefined
      );
      const url = new URL(window.location.href);
      url.searchParams.delete("imageSearchUrl");
      window.history.replaceState(null, "", url.toString());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When arriving via ?share=<token> (SaveInput's share icon copies
  // /search?share=<token> to the clipboard), fetch that filter's criteria
  // from the public, unauthenticated redemption endpoint and apply it —
  // this is the piece that was missing entirely, so shared links silently
  // landed on the plain search page instead of the sender's filter.
  useEffect(() => {
    if (!sharedFilterToken) return;

    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/v2/filters/shared/${sharedFilterToken}`);
        if (!res.ok) {
          const body = await res.text().catch(() => "<unreadable>");
          console.error(
            `[share] GET /api/v2/filters/shared/${sharedFilterToken} → ${res.status}`,
            body
          );
          notifications.show({
            title: "Couldn't load shared filter",
            message: "This shared filter link is invalid or no longer active.",
            color: "red",
            autoClose: 2500,
          });
          // Let the restore-guard effects below fall back to this browser's
          // own saved filter, same as a normal (non-shared) page load —
          // setting this unblocks their `sharedFilterToken` guard instead of
          // calling selectSavedFilter directly here, since this effect's
          // empty dependency array means its closure is permanently stale
          // (bound to the not-yet-loaded savedFilters from first render).
          setShareRedeemFailed(true);
          return;
        }
        const json = await res.json();
        const rawSearchFilter = json?.data?.search_filter;
        // search_filter comes back straight from the DB column, which stores
        // it key-compressed (see compressFilters/lib/client/filters.ts) —
        // the same treatment loadSavedFilters gives every other saved filter
        // before it's usable. Skipping this step was the actual bug: the
        // decompressed shape's `filters` key literally doesn't exist yet on
        // the raw payload (it's under the compressed key "a"), so it always
        // read as undefined here, regardless of whether the row itself was
        // ever valid.
        const searchFilter = rawSearchFilter
          ? (migrateFilterFormat(decompressFilters(rawSearchFilter)) as unknown as SavedFilter)
          : null;

        if (searchFilter && isValidActiveFilters(searchFilter.filters)) {
          applySharedFilter(searchFilter);
        } else {
          // Reachable if the row is otherwise malformed (e.g. predates this
          // shape entirely) — just as unusable to the recipient as a dead
          // token, and must not be handed to applySharedFilter as-is: without
          // this check it would set activeFilters to undefined, crashing
          // every component that reads activeFilters.<field> without a
          // leading `?.`.
          console.error(
            `[share] GET /api/v2/filters/shared/${sharedFilterToken} returned an unusable payload`,
            { raw: rawSearchFilter, decompressed: searchFilter }
          );
          notifications.show({
            title: "Couldn't load shared filter",
            message: "This shared filter link is invalid or no longer active.",
            color: "red",
            autoClose: 2500,
          });
          setShareRedeemFailed(true);
        }
      } catch (error) {
        console.error(`[share] GET /api/v2/filters/shared/${sharedFilterToken} threw`, error);
        notifications.show({
          title: "Couldn't load shared filter",
          message: "Something went wrong loading this shared filter.",
          color: "red",
          autoClose: 2500,
        });
        setShareRedeemFailed(true);
      } finally {
        const url = new URL(window.location.href);
        url.searchParams.delete("share");
        window.history.replaceState(null, "", url.toString());
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When arriving from another page (e.g. product page) with ?clearFilters=1,
  // reset filters so they don't bleed into the image search.
  useEffect(() => {
    if (clearFilters) {
      resetActiveFilters();
      router.replace("/search", { scroll: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearFilters]);
  const { isOpen: tipsOpen, openIfUnseen: openTipsIfUnseen, close: closeTips } = useSearchTips();

  // Desktop: show once on first visit to the search page
  useEffect(() => {
    if (!isMobile) openTipsIfUnseen();
  }, [isMobile, openTipsIfUnseen]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [loadedBrand, setLoadedBrand] = useState<{ label: string; value: number }>();

  // Enable filter change announcements for screen readers
  useFilterAnnouncements();

  // Track scroll depth in search results
  useScrollDepthTracking(items.length, loading);

  useEffect(() => {
    if (brand) {
      const decodedBrand = decodeURIComponent(brand).toLowerCase();
      const foundBrand = loadedBrands?.find(
        (storedBrand) =>
          generateBrandSlug(storedBrand.label) === decodedBrand ||
          storedBrand.label.toLowerCase() === decodedBrand
      );
      if (foundBrand) {
        setPreloadBrand(foundBrand.label);
        setLoadedBrand({
          label: foundBrand.label,
          value: foundBrand.id,
        });
      }
    }
  }, [loadedBrands, brand, setPreloadBrand]);

  useEffect(() => {
    if (vendor) {
      const decodedVendor = decodeURIComponent(vendor).toLowerCase();
      const foundVendor = loadedVendors?.find(
        (storedVendor) =>
          generateVendorSlug(storedVendor.label) === decodedVendor ||
          storedVendor.label.toLowerCase() === decodedVendor
      );
      if (foundVendor) {
        setPreloadVendor(foundVendor.label);
      }
    }
  }, [loadedVendors, vendor, setPreloadVendor]);

  useEffect(() => {
    if (type) {
      const decodedType = decodeURIComponent(type);
      setPreloadedType(decodedType);
    }
  }, [type, setPreloadedType]);

  // Handle searchText from URL params (used for brand model keywords)
  useEffect(() => {
    if (searchText) {
      const decodedSearchText = decodeURIComponent(searchText);
      updateKeywordSearch(decodedSearchText);
    }
  }, [searchText, updateKeywordSearch]);

  useEffect(() => {
    if (!loadedVendors || !loadedBrands) {
      return;
    }

    if (regions || brands || vendors || keywords) {
      setPreloadedFilters();
    }
  }, [loadedVendors, loadedBrands, regions, brands, vendors, keywords, setPreloadedFilters]);

  useEffect(() => {
    if (!savedFilters?.length || savedFilterRestored) return;
    // URL has preloaded params (e.g. Latest Drops) — let setPreloadedFilters handle state, don't restore saved filter
    if (vendors?.length || brands?.length || regions?.length || keywords?.length) return;
    // A shared link takes priority over whatever saved filter this browser
    // last had persisted — this effect and the ?share= fetch effect below
    // both land here async (one waits on savedFilters loading, the other on
    // a network round trip), and without this guard whichever resolves last
    // silently wins, making the shared filter apply intermittently for any
    // signed-in recipient who already had a saved filter selected. Once the
    // share redemption fails, shareRedeemFailed unblocks this guard so the
    // user's own saved filter can be restored instead.
    if (sharedFilterToken && !shareRedeemFailed) return;

    if (filterId) {
      selectSavedFilter(filterId, true);
      setSavedFilterRestored(true);
    } else if (savedFilterId) {
      selectSavedFilter(savedFilterId, true);
      setSavedFilterRestored(true);
    }
  }, [
    filterId,
    savedFilterId,
    selectSavedFilter,
    savedFilters,
    savedFilterRestored,
    vendors,
    brands,
    regions,
    keywords,
    sharedFilterToken,
    shareRedeemFailed,
  ]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    // Same priority as above: a shared link wins over a ?preset= deep link.
    if (sharedFilterToken && !shareRedeemFailed) return;

    const searchParams = new URLSearchParams(window.location.search);
    const presetId = searchParams.get("preset");

    if (presetId && savedFilters?.length && !savedFilterRestored) {
      const presetIdNum = parseInt(presetId, 10);
      if (!isNaN(presetIdNum)) {
        selectSavedFilter(presetIdNum, true);
        setSavedFilterRestored(true);
      }
    }
  }, [savedFilters, selectSavedFilter, savedFilterRestored, sharedFilterToken, shareRedeemFailed]);

  // Signed-out visitors never get a token budget initialized (see
  // globalContext.tsx's early return for !isSignedIn), so tokensInitialized
  // can never become true for them — which silently made the promo card
  // disappear for exactly the audience it originally targeted when this
  // condition was narrowed from !isProUser to a token-exhaustion check.
  // Restore visibility for them with sign-up-focused copy instead, while
  // keeping the out-of-tokens ad for signed-in users who've used up today's budget.
  const showProAd = !isSignedIn || (TOKEN_UI_ENABLED && tokensInitialized && tokensRemaining === 0);
  const proAdVariant = isSignedIn ? "tokens" : "signup";

  return (
    <>
      <SearchTipsModal opened={tipsOpen} onClose={closeTips} />
      <MobileMenu />
      <MantineBox
        className={styles.searchPage}
        ref={scrollContainerRef}
        id="masonary"
        data-slidein-open={itemModalOpen || favoritesModalOpen || priceMonitorOpen}
      >
        <MantineBox className={styles.filterMenuContainer}>
          <FilterMenu key={isMobile ? "mobile" : "desktop"} imageSearchMode={imageSearchActive} />
        </MantineBox>
        {imageSearchActive ? (
          <MantineBox className={styles.imageSearchContent}>
            <ImageSearchBanner />
            <CardGrid
              items={imageSearchItems}
              loading={imageSearchLoading || imageSearchLoadingMore}
              noResults={!imageSearchLoading && imageSearchItems.length === 0}
              enableImageSearch={enableImageSearch}
            />
          </MantineBox>
        ) : (
          <MantineBox className={styles.resultsContainer}>
            {!kurboMode && recentItemCount > 0 && <KurboPlaceholderCard count={recentItemCount} />}
            <VirtualizedCardGrid
              items={items}
              loading={loading}
              noResults={noResults}
              showProAd={showProAd}
              proAdVariant={proAdVariant}
              enableImageSearch={enableImageSearch}
              scrollContainerRef={scrollContainerRef}
              onLoadMore={loadMore}
              onResumePositionChange={handleResumePositionChange}
              scrollToItemId={scrollToItemId}
              isSlideInOpen={itemModalOpen}
            />
            {noResults && !loading && <NoResultsRescue />}
            {hasReachedEnd && !loading && items.length > 0 && <FeedEndCta />}
          </MantineBox>
        )}
      </MantineBox>
      {showSignInPrompt && <SignInPrompt onClose={() => setShowSignInPrompt(false)} />}
    </>
  );
};

export default PageContent;
